<template>
  <aside
    class="w-64 bg-white dark:bg-dark-bg text-slate-500 dark:text-slate-400 h-screen flex flex-col shadow-xl border-r border-slate-200 dark:border-white/5"
  >
    <!-- LOGO -->
    <div class="p-6 flex items-center gap-3 border-b dark:border-white/5">
      <div
        class="w-9 h-9 bg-primary rounded-xl flex items-center justify-center text-white font-bold text-xl"
      >
        R
      </div>

      <span class="text-xl font-black uppercase italic text-slate-800 dark:text-white">
        Ridey<span class="text-primary">Admin</span>
      </span>
    </div>

    <!-- NAV -->
    <nav class="flex-1 mt-6 overflow-y-auto px-4 pb-4 custom-scrollbar">
      <ul class="space-y-2">

        <!-- DASHBOARD -->
        <li>
          <router-link to="/dashboard" class="nav-link" active-class="active-link">
            <i class="fas fa-chart-line icon"></i>
            {{ $t("dashboard") }}
          </router-link>
        </li>

        <!-- USERS -->
        <template v-if="isAdmin">
          <li class="section-title">{{ $t("users") }}</li>

          <li>
            <router-link to="/users" class="nav-link" active-class="active-link">
              <i class="fas fa-users icon"></i>
              {{ $t("users_list") }}
            </router-link>
          </li>

          <li>
            <router-link to="/owners" class="nav-link" active-class="active-link">
              <i class="fas fa-id-badge icon"></i>
              {{ $t("owners_management") }}
            </router-link>
          </li>

          <li>
            <router-link to="/roles" class="nav-link" active-class="active-link">
              <i class="fas fa-shield-halved icon"></i>
              {{ $t("roles") }}
            </router-link>
          </li>

          <li>
            <router-link to="/admin/chats" class="nav-link" active-class="active-link">
              <i class="fas fa-comments icon"></i>
              {{ $t("messages") }}
            </router-link>
          </li>
        </template>

        <!-- FLEET -->
        <li class="section-title">{{ $t("fleet") }}</li>

        <li>
          <router-link to="/cars" class="nav-link" active-class="active-link">
            <i class="fas fa-car icon"></i>
            {{ $t("cars") }}
          </router-link>
        </li>

        <!-- BOOKINGS -->
        <li v-if="isAdmin">
          <router-link to="/admin/bookings" class="nav-link" active-class="active-link">
            <i class="fas fa-calendar-check icon"></i>
            {{ $t("bookings") }}
          </router-link>
        </li>

        <li v-if="canAccessPanel && !isAdmin">
          <router-link to="/host/bookings" class="nav-link" active-class="active-link">
            <i class="fas fa-file-invoice icon"></i>
            {{ $t("my_bookings") }}
          </router-link>
        </li>

        <!-- MASTER DATA -->
        <template v-if="isAdmin">

          <li class="section-title">{{ $t("master_data") }}</li>

          <li>
            <router-link to="/brand" class="nav-link" active-class="active-link">
              <i class="fas fa-tags icon"></i>
              {{ $t("brand") }}
            </router-link>
          </li>

          <li>
            <router-link to="/categories" class="nav-link" active-class="active-link">
              <i class="fas fa-folder-tree icon"></i>
              {{ $t("category") }}
            </router-link>
          </li>

          <!-- ===================== -->
          <!-- SYSTEM DATA DROPDOWN -->
          <!-- ===================== -->

          <li class="section-title">{{ $t("system_data") }}</li>

          <!-- Toggle -->
          <li>
            <button
              @click="openSystemData = !openSystemData"
              class="nav-link w-full flex justify-between items-center"
            >
              <span class="flex items-center gap-3">
                <i class="fas fa-database icon"></i>
                {{ $t("system_data") }}
              </span>

              <i
                class="fas fa-chevron-down transition-transform duration-300"
                :class="{ 'rotate-180': openSystemData }"
              ></i>
            </button>
          </li>

          <!-- DROPDOWN ITEMS (NO LOSS OF DATA) -->
          <transition name="fade">
            <div v-show="openSystemData" class="pl-4 space-y-2 mt-2">

              <li v-for="item in masterDataLinks" :key="item.to">
                <router-link
                  :to="item.to"
                  class="nav-link text-xs"
                  active-class="active-link"
                >
                  <i :class="item.icon + ' icon'"></i>
                  {{ $t(item.label) }}
                </router-link>
              </li>

            </div>
          </transition>

        </template>

      </ul>
    </nav>

    <!-- USER -->
    <div class="p-4 border-t dark:border-white/5">
      <button @click="logout" class="nav-link w-full justify-center hover:bg-red-500 hover:text-white">
        <i class="fas fa-right-from-bracket icon"></i>
        {{ $t("logout") }}
      </button>
    </div>
  </aside>
</template>

<script>
import authService from "@/services/auth.service";

export default {
  name: "Sidebar",

  data() {
    return {
      openSystemData: true,
    };
  },

  computed: {
    user() {
      return authService.getUser();
    },

    roles() {
      return this.user?.roles || [];
    },

    isAdmin() {
      return this.roles.includes("admin");
    },

    canAccessPanel() {
      return this.roles.includes("admin") || this.roles.includes("host");
    },

    masterDataLinks() {
      return [
        { to: "/master-data/vehicle-types", label: "vehicle_types", icon: "fas fa-truck-pickup" },
        { to: "/master-data/fuel-types", label: "fuel_types", icon: "fas fa-gas-pump" },
        { to: "/master-data/transmission-types", label: "transmission_types", icon: "fas fa-cog" },
        { to: "/master-data/features", label: "features", icon: "fas fa-star" },
        { to: "/master-data/pickup-options", label: "pickup_options", icon: "fas fa-map-location-dot" },
        { to: "/master-data/elevate-experiences", label: "elevate_experiences", icon: "fas fa-gem" },
        { to: "/master-data/car-rules", label: "car_rules", icon: "fas fa-list-check" },
        { to: "/master-data/protection-plans", label: "protection_plans", icon: "fas fa-file-shield" },
        { to: "/master-data/extras", label: "extras", icon: "fas fa-plus-circle" },
      ];
    },
  },

  methods: {
    logout() {
      authService.logout();
      this.$router.push("/login");
    },
  },
};
</script>
<style scoped>
.nav-link {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 14px;
  color: #64748b; /* slate-500 */
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid transparent;
}

.nav-link:hover {
  background-color: #f1f5f9; /* bg-slate-100 */
  color: #0f172a; /* text-slate-900 */
  border-color: rgba(0, 0, 0, 0.05);
  transform: translateY(-1px);
}

:deep(.dark) .nav-link:hover,
.dark .nav-link:hover {
  background-color: #1e2330;
  color: #f8fafc;
  border-color: rgba(255, 255, 255, 0.05);
}

.active-link {
  background-color: #3d5cff !important;
  color: white !important;
  box-shadow: 0 8px 20px rgba(61, 92, 255, 0.3);
  border-color: rgba(255, 255, 255, 0.1);
}

.section-title {
  padding: 1.5rem 1rem 0.5rem 1rem;
  font-size: 0.65rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  color: #94a3b8; /* slate-400 */
}

:deep(.dark) .section-title,
.dark .section-title {
  color: #334155; /* slate-700 */
}

.icon {
  font-size: 1.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
}

/* Custom Scrollbar */
.custom-scrollbar::-webkit-scrollbar {
  width: 3px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background-color: #e2e8f0; /* bg-slate-200 */
  border-radius: 10px;
}

:deep(.dark) .custom-scrollbar::-webkit-scrollbar-thumb,
.dark .custom-scrollbar::-webkit-scrollbar-thumb {
  background-color: #1e2330; /* dark-input */
}
</style>
